import { useEffect, useState } from "react";
import { ThinkingOrb, type OrbSize, type OrbState } from "thinking-orbs";

export type { OrbState };

// shadcn tokens are bare HSL triplets ("0 0% 45.1%"), but the orb's canvas needs a hex color
const hslTripletToHex = (triplet: string): string | undefined => {
  const match = triplet.trim().match(/^(-?[\d.]+)(?:deg)?\s+([\d.]+)%\s+([\d.]+)%$/);
  if (!match) return undefined;
  const h = ((Number(match[1]) % 360) + 360) % 360;
  const s = Number(match[2]) / 100;
  const l = Number(match[3]) / 100;
  const a = s * Math.min(l, 1 - l);
  const channel = (n: number) => {
    const k = (n + h / 30) % 12;
    const value = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(value * 255).toString(16).padStart(2, "0");
  };
  return `#${channel(0)}${channel(8)}${channel(4)}`;
};

interface OrbInk {
  color?: string;
  theme: "light" | "dark";
}

const readInk = (): OrbInk => {
  const root = document.documentElement;
  return {
    color: hslTripletToHex(getComputedStyle(root).getPropertyValue("--muted-foreground")),
    // Follow the app's .dark class rather than the OS setting ("auto" falls back to the OS)
    theme: root.classList.contains("dark") ? "dark" : "light",
  };
};

// Muted-foreground ink for the current theme, updated when <html> changes class (dark mode)
// or inline style (runtime workspace theming)
const useMutedInk = (): OrbInk => {
  const [ink, setInk] = useState(readInk);
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const next = readInk();
      setInk((prev) => (prev.color === next.color && prev.theme === next.theme ? prev : next));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class", "style"] });
    return () => observer.disconnect();
  }, []);
  return ink;
};

interface AgentOrbProps {
  state?: OrbState;
  size?: OrbSize;
  className?: string;
}

/**
 * Animated "agent is working" orb drawn in the muted-foreground token. Decorative: pair it
 * with a visible status label inside a role="status" region, which screen readers announce.
 */
export const AgentOrb = ({ state = "working", size = 20, className }: AgentOrbProps) => {
  const { color, theme } = useMutedInk();
  return (
    <ThinkingOrb
      state={state}
      size={size}
      theme={theme}
      color={color}
      aria-hidden="true"
      className={className}
    />
  );
};
